import api from "../../api";
import { useEffect, useState, useRef } from "react";
// تغییر ایمپورت به کامپوننت جدید نشان
import NeshanMap from "../../components/Map/NeshanMap";
import { toast } from "react-hot-toast";
import { FiTrash2 } from "react-icons/fi";

// تغییر پیش‌فرض‌ها به ساختار [lng, lat]
const DEFAULT_CENTER = [51.356829, 35.713556];
const DEFAULT_ZOOM = 14;
const ADDRESS_ZOOM = 15;

const isValidCoordinate = (value) => {
  return Number.isFinite(Number(value));
};

const hasValidCoordinates = (latitude, longitude) => {
  return (
    isValidCoordinate(latitude) &&
    isValidCoordinate(longitude)
  );
};

const normalizeId = (value) => {
  if (value === null || value === undefined) return "";
  if (typeof value === "object") return String(value.id ?? "");
  return String(value);
};

const normalizeCoordinate = (value) => {
  if (!isValidCoordinate(value)) return "";
  return Number(value);
};

const getInitialFormData = ({ address, address_type }) => {
  return {
    title: address?.title || "",
    address_type: address?.address_type || address_type || "",
    province: normalizeId(address?.province),
    city: normalizeId(address?.city),
    address_text: address?.address_text || "",
    postal_code: address?.postal_code || "",
    latitude: normalizeCoordinate(address?.latitude),
    longitude: normalizeCoordinate(address?.longitude),
    is_default: Boolean(address?.is_default),
    is_active: address?.is_active ?? true,
  };
};

const getInitialMapConfig = (address) => {
  if (hasValidCoordinates(address?.latitude, address?.longitude)) {
    // تبدیل به فرمت [lng, lat] برای استفاده در نشان
    return {
      center: [Number(address.longitude), Number(address.latitude)],
      zoom: ADDRESS_ZOOM,
    };
  }
  return { center: DEFAULT_CENTER, zoom: DEFAULT_ZOOM };
};

export default function AddressFormModal({
  address,
  isEditing,
  onClose,
  onSuccess,
  address_type,
  user,
}) {
  const [formData, setFormData] = useState(() =>
    getInitialFormData({ address, address_type })
  );

  const [mapConfig, setMapConfig] = useState(() =>
    getInitialMapConfig(address)
  );

  const [provinces, setProvinces] = useState([]);
  const [cities, setCities] = useState([]);
  const [loading, setLoading] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [fetchingCities, setFetchingCities] = useState(false);
  const [error, setError] = useState("");

  const userMovedMarkerRef = useRef(false);
  const initializedAddressIdRef = useRef(null);
  const isFirstLoadRef = useRef(true);

  useEffect(() => {
    if (!address) return;
    if (initializedAddressIdRef.current === address.id) return;
    initializedAddressIdRef.current = address.id;
    userMovedMarkerRef.current = false;
    isFirstLoadRef.current = true;

    setFormData(getInitialFormData({ address, address_type }));

    if (hasValidCoordinates(address.latitude, address.longitude)) {
      // تبدیل به فرمت [lng, lat]
      setMapConfig({
        center: [Number(address.longitude), Number(address.latitude)],
        zoom: ADDRESS_ZOOM,
      });
    }
  }, [address?.id]);

  useEffect(() => {
    const fetchProvinces = async () => {
      try {
        const response = await api.get("/accounts/provinces/");
        setProvinces(response.data || []);
      } catch (err) {
        setError("خطا در دریافت لیست استان‌ها");
      }
    };
    fetchProvinces();
  }, []);

  useEffect(() => {
    if (!formData.province) {
      setCities([]);
      return;
    }

    const fetchCities = async () => {
      setFetchingCities(true);
      try {
        const response = await api.get(`/accounts/cities/?province=${formData.province}`);
        setCities(response.data);

        if (isEditing && formData.city && !userMovedMarkerRef.current) {
          const currentCity = response.data.find(c => String(c.id) === String(formData.city));
          if (currentCity && hasValidCoordinates(currentCity.latitude, currentCity.longitude)) {
            if (!hasValidCoordinates(formData.latitude, formData.longitude) || isFirstLoadRef.current) {
              isFirstLoadRef.current = false;
              updateMapFromCity(currentCity);
            }
          }
        }
      } catch (err) {
        setError("خطا در دریافت لیست شهرها");
      } finally {
        setFetchingCities(false);
      }
    };
    fetchCities();
  }, [formData.province]);

  const updateMapFromCity = (cityData) => {
    if (!cityData || !hasValidCoordinates(cityData.latitude, cityData.longitude)) return;
    // مرکز جدید برای نشان به صورت [lng, lat] تعریف می‌شود
    const newCenter = [Number(cityData.longitude), Number(cityData.latitude)];
    setMapConfig({ center: newCenter, zoom: Number(cityData.zoom_level) || 12 });
    setFormData(prev => ({
      ...prev,
      latitude: Number(cityData.latitude),
      longitude: Number(cityData.longitude)
    }));
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    if (name === "province") {
      setFormData(prev => ({ ...prev, province: value, city: "", latitude: "", longitude: "" }));
      setCities([]);
      setMapConfig({ center: DEFAULT_CENTER, zoom: DEFAULT_ZOOM });
      return;
    }
    if (name === "city") {
      const selectedCity = cities.find(c => String(c.id) === String(value));
      setFormData(prev => ({
        ...prev,
        city: value,
        latitude: selectedCity ? normalizeCoordinate(selectedCity.latitude) : "",
        longitude: selectedCity ? normalizeCoordinate(selectedCity.longitude) : "",
      }));
      if (selectedCity) {
        updateMapFromCity(selectedCity);
        userMovedMarkerRef.current = false;
      }
      return;
    }
    setFormData(prev => ({ ...prev, [name]: type === "checkbox" ? checked : value }));
  };

  // دریافت مختصات [lng, lat] از نقشه نشان
  const handleMarkerChange = (lngLat) => {
    userMovedMarkerRef.current = true;
    isFirstLoadRef.current = false;
    const lng = Number(lngLat[0]);
    const lat = Number(lngLat[1]);
    setFormData(prev => ({ ...prev, latitude: lat, longitude: lng }));
    setMapConfig(prev => ({ ...prev, center: [lng, lat] }));
  };

  const validate = () => {
    if (!formData.title.trim()) { toast.error("عنوان آدرس الزامی است"); return false; }
    if (!formData.province) { toast.error("استان را انتخاب کنید"); return false; }
    if (!formData.city) { toast.error("شهر را انتخاب کنید"); return false; }
    if (!formData.postal_code || formData.postal_code.trim().length !== 10) {
      toast.error("کد پستی ۱۰ رقمی معتبر وارد کنید"); return false;
    }
    if (formData.address_type === "warehouse" && !hasValidCoordinates(formData.latitude, formData.longitude)) {
      toast.error("تعیین موقعیت روی نقشه برای انبار الزامی است"); return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setLoading(true);
    setError("");

    try {
      const dataToSend = {
        ...formData,
        province: Number(formData.province),
        city: Number(formData.city),
        // گرد کردن مختصات قبل از ارسال به بک‌اند
        latitude: formData.latitude ? Math.round(Number(formData.latitude) * 1000000) / 1000000 : null,
        longitude: formData.longitude ? Math.round(Number(formData.longitude) * 1000000) / 1000000 : null,
      };

      const url = isEditing
        ? user === "user" ? `/accounts/me/addresses/${address.id}/` : `/accounts/admin/addresses/${address.id}/`
        : "/accounts/me/addresses/";

      const response = await api[isEditing ? "patch" : "post"](url, dataToSend);
      toast.success(isEditing ? "ویرایش شد" : "ثبت شد");
      onSuccess(response.data);
      onClose();
    } catch (err) {
      setError("خطا در ذخیره اطلاعات");
      toast.error("عملیات ناموفق بود");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm("آیا از حذف این آدرس اطمینان دارید؟")) return;

    setDeleting(true);
    try {
      const url = user === "user"
        ? `/accounts/me/addresses/${address.id}/`
        : `/accounts/admin/addresses/${address.id}/`;

      await api.delete(url);
      toast.success("آدرس با موفقیت حذف شد");
      onSuccess();
      onClose();
    } catch (err) {
      toast.error("خطا در حذف آدرس");
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[1001] flex items-center justify-center overflow-y-auto bg-black/60 backdrop-blur-sm p-4">
      <div className="relative my-auto w-full max-w-5xl rounded-2xl bg-white shadow-2xl animate-in zoom-in duration-200">
        <button type="button" onClick={onClose} className="absolute left-4 top-4 z-10 text-gray-400 hover:text-gray-600">
          <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>

        <div className="px-6 py-8">
          <h2 className="mb-6 text-xl font-bold text-gray-800">
            {isEditing ? "ویرایش آدرس" : "افزودن آدرس جدید"}
          </h2>

          <form onSubmit={handleSubmit}>
            <div className="grid grid-cols-1 gap-8 md:grid-cols-2">
              <div className="space-y-4">
                <div className="grid grid-cols-3 items-center gap-3">
                  <label className="text-xs font-bold text-gray-700">عنوان <span
                      className="text-red-500">*</span></label>
                  <input type="text" name="title" value={formData.title} onChange={handleChange}
                         className="col-span-2 rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-blue-500"/>
                </div>

                <div className="grid grid-cols-3 items-center gap-3">
                  <label className="text-xs font-bold text-gray-700">استان <span
                      className="text-red-500">*</span></label>
                  <select name="province" value={formData.province} onChange={handleChange}
                          className="col-span-2 rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-sm outline-none">
                    <option value="">انتخاب استان</option>
                    {provinces.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                  </select>
                </div>

                <div className="grid grid-cols-3 items-center gap-3">
                  <label className="text-xs font-bold text-gray-700">شهر <span className="text-red-500">*</span></label>
                  <select name="city" value={formData.city} onChange={handleChange}
                          disabled={!formData.province || fetchingCities}
                          className="col-span-2 rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-sm disabled:opacity-50">
                    <option value="">{fetchingCities ? "در حال دریافت..." : "انتخاب شهر"}</option>
                    {cities.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                  </select>
                </div>

                <div className="grid grid-cols-3 items-start gap-3">
                  <label className="mt-2 text-xs font-bold text-gray-700">آدرس</label>
                  <textarea name="address_text" value={formData.address_text} onChange={handleChange} rows={2}
                            className="col-span-2 resize-none rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-blue-500"/>
                </div>

                <div className="grid grid-cols-3 items-center gap-3">
                  <label className="text-xs font-bold text-gray-700">کد پستی <span
                      className="text-red-500">*</span></label>
                  <input type="text" name="postal_code" value={formData.postal_code} onChange={handleChange}
                         maxLength={10}
                         className="col-span-2 rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-sm"/>
                </div>

                <div className="flex gap-6 pt-2 pr-[33%]">
                  <label className="flex cursor-pointer items-center gap-2 text-sm">
                    <input type="checkbox" name="is_default" checked={formData.is_default} onChange={handleChange}
                           className="h-4 w-4 rounded text-blue-600"/>
                    آدرس پیش‌فرض
                  </label>
                  <label className="flex cursor-pointer items-center gap-2 text-sm">
                    <input type="checkbox" name="is_active" checked={formData.is_active} onChange={handleChange}
                           className="h-4 w-4 rounded text-blue-600"/>
                    فعال
                  </label>
                </div>
              </div>

              <div className="flex h-[350px] flex-col space-y-3 md:h-[450px]">
                <label className="text-xs font-bold text-gray-700">
                  موقعیت روی نقشه
                </label>

                <div
                    className="relative flex-1 overflow-hidden rounded-xl border border-gray-200 bg-gray-100 shadow-sm">
                  <NeshanMap
                      key={
                        isEditing
                            ? `neshan-map-${address?.id}`
                            : "new-neshan-map"
                      }
                      center={mapConfig.center}
                      zoom={mapConfig.zoom}
                      onChange={handleMarkerChange}
                  />
                </div>
              </div>

            </div>

            <div className="mt-8 flex flex-wrap items-center justify-between gap-4 border-t pt-6">
              <div className="flex gap-3">
                <button
                    type="submit"
                    disabled={loading || deleting}
                    className="rounded-xl bg-blue-800 px-10 py-2.5 text-sm font-bold text-white shadow-md transition hover:bg-blue-900 disabled:opacity-50"
                >
                  {loading ? "در حال ذخیره..." : "تأیید و ثبت نهایی"}
                </button>
                <button
                  type="button"
                  onClick={onClose}
                  className="rounded-xl bg-gray-100 px-8 py-2.5 text-sm font-semibold text-gray-600 transition hover:bg-gray-200"
                >
                  انصراف
                </button>
              </div>

              {isEditing && (
                <button
                  type="button"
                  onClick={handleDelete}
                  disabled={deleting || loading}
                  className="flex items-center gap-2 rounded-xl border border-red-200 bg-red-50 px-5 py-2.5 text-sm font-bold text-red-600 transition hover:bg-red-100 disabled:opacity-50"
                >
                  <FiTrash2 size={18} />
                  {deleting ? "در حال حذف..." : "حذف این آدرس"}
                </button>
              )}
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
